import { useState } from 'react';
import { useRouter } from 'next/router';
import { isAiConfigAvailableForUser } from 'shared-helpers/aiTools';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import {
  Alert,
  EmptyIndicator,
  FlagIcon,
  LoadingIndicator,
  ModalButtonWithForm,
  RichTextValue
} from 'components/common';
import {
  Box,
  BoxTable,
  BoxTableCell,
  BoxTableHeader,
  BoxTableRow
} from 'components/common/box';
import { FormGroup, RadioGroup } from 'components/common/form';
import { getCourseLanguageVersionOptions } from 'lib/courses';

import type { Course } from 'shared-types';

interface Props {
  onTranslationSuccess?: () => void;
  targetLanguage: Course['language'];
}

type FormValues = {
  overwriteExisting: boolean;
};

type GenerationState = 'preview' | 'generating' | 'completed';

/**
 * Modal for AI-powered translation generation of a course language version.
 * Shows original and translated strings, triggers generation via API,
 * and allows navigating to the editor to review results.
 * Only renders when aiConfig is enabled for user.
 */
const AIGenerateTranslations = ({
  onTranslationSuccess,
  targetLanguage
}: Props) => {
  const { dispatch } = useDispatch();
  const router = useRouter();

  const {
    messages,
    pageState: { course },
    serverConfig,
    theme,
    user
  } = useReduxState();

  const [generationState, setGenerationState] =
    useState<GenerationState>('preview');
  const [isLoading, setIsLoading] = useState(false);
  const [numberOfFailedTexts, setNumberOfFailedTexts] = useState(0);
  const [overwriteExisting, setOverwriteExisting] = useState(false);
  const [translatedKeys, setTranslatedKeys] = useState<Set<string>>(new Set());
  const [originalData, setOriginalData] = useState<Record<
    string,
    string
  > | null>(null);
  const [translationData, setTranslationData] = useState<Record<
    string,
    string
  > | null>(null);

  const aiEnabled = isAiConfigAvailableForUser({ serverConfig, user });

  if (!aiEnabled) return null;

  const isGenerating = generationState === 'generating';
  const isCompleted = generationState === 'completed';

  const languageOptions = getCourseLanguageVersionOptions({ course });

  const formatLanguageLabel = (lang: string) => {
    const name = languageOptions[lang]?.capitalizedName;
    return name ? `${name} (${lang})` : lang;
  };

  const fetchData = async (): Promise<boolean> => {
    setIsLoading(true);
    try {
      const [originalResponse, translationResponse] = await Promise.all([
        api.get(
          `/admin/courses/${course.id}/language-versions-data-export/${course.language}`
        ),
        api.get(
          `/admin/courses/${course.id}/language-versions-data-export/${targetLanguage}`
        )
      ]);
      setOriginalData(originalResponse.data);
      setTranslationData(translationResponse.data);
      return true;
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleShow = () => fetchData();
  const handleHide = () => {
    setOriginalData(null);
    setTranslationData(null);
    setGenerationState('preview');
    setNumberOfFailedTexts(0);
    setOverwriteExisting(false);
    setTranslatedKeys(new Set());
  };

  const navigateToEditor = () => {
    const firstChapter = course.chapters?.[0];
    if (firstChapter) {
      router.push(
        `/admin/courses/${course.slug}/edit/${firstChapter.slug}?languageVersion=${targetLanguage}`
      );
    } else {
      router.push(
        `/admin/courses/${course.slug}/edit?languageVersion=${targetLanguage}`
      );
    }
  };

  // Completed: only entries the server actually translated.
  // Overwrite: all non-empty source entries.
  // Default: only entries with no existing translation (same emptiness check
  // as server-side exportLanguageVersionData).
  const getVisibleEntries = (overwriteExisting: boolean) => {
    const entries = originalData
      ? Object.entries(originalData).filter(
          ([key, value]) => Boolean(value) && !key.startsWith('course__')
        )
      : [];

    if (isCompleted) {
      return entries.filter(([key]) => translatedKeys.has(key));
    }
    if (overwriteExisting) return entries;
    return entries.filter(([key]) => {
      const translatedValue = translationData?.[key] ?? '';
      return !translatedValue || translatedValue === '<p></p>';
    });
  };

  const handleSubmit = async (values: Partial<FormValues>) => {
    if (isCompleted) {
      navigateToEditor();
      return true;
    }

    const overwriteExisting = Boolean(values.overwriteExisting);
    if (getVisibleEntries(overwriteExisting).length === 0) return false;

    setGenerationState('generating');
    try {
      const { data } = await api.post(
        `/admin/courses/${course.id}/language-versions/${targetLanguage}/ai-translations/generate`,
        { scope: 'course', overwriteExisting }
      );
      const fetchSucceeded = await fetchData();
      if (!fetchSucceeded) {
        setGenerationState('preview');
        return false;
      }
      setNumberOfFailedTexts(data.numberOfKeysFailed);
      setTranslatedKeys(new Set<string>(data.translatedKeys));
      setGenerationState('completed');
      if (onTranslationSuccess) onTranslationSuccess();
      return false;
    } catch (err) {
      setGenerationState('preview');
      throw err;
    }
  };

  const initialValues: FormValues = { overwriteExisting: false };

  const overwriteOptions = [
    {
      id: 'ignore-existing',
      label: messages.aiGenerateTranslationsIgnoreExisting,
      value: false
    },
    {
      id: 'overwrite-existing',
      label: messages.aiGenerateTranslationsOverwriteExisting,
      value: true
    }
  ];

  return (
    <ModalButtonWithForm
      buttonIcon="wand-magic-sparkles"
      buttonIsDropdown={true}
      buttonText={messages.aiGenerateTranslationsTranslateWithAi}
      confirmChanges={false}
      contentStyle={{ backgroundColor: theme.lightGreyLighter }}
      formOnValueChange={(values: Partial<FormValues>) =>
        setOverwriteExisting(Boolean(values.overwriteExisting))
      }
      initialValues={initialValues}
      modalActionCategory="success"
      modalActionText={
        isCompleted
          ? messages.aiGenerateTranslationsReviewInEditor
          : messages.commonTranslate
      }
      modalCloseButton={isGenerating || isCompleted}
      modalTitle={messages.aiGenerateTranslationsTranslateWithAi}
      modalWidth="wide"
      onHide={handleHide}
      onShow={handleShow}
      onSubmit={handleSubmit}
      submitDisabled={
        isLoading ||
        isGenerating ||
        (!isCompleted && getVisibleEntries(overwriteExisting).length === 0)
      }
    >
      {({ values }: { values: Partial<FormValues> }) => {
        if (isGenerating) {
          return (
            <Box padding="30px 20px 40px" style={{ textAlign: 'center' }}>
              <LoadingIndicator padding={10} />
              <p
                style={{
                  color: theme.textColorLighter,
                  fontSize: '1.6rem',
                  fontWeight: 700,
                  margin: '10px 0 5px'
                }}
              >
                {messages.aiGenerateTranslationsGenerating}&hellip;
              </p>
              <p
                style={{
                  color: theme.textColorLighter,
                  fontSize: '1.5rem',
                  margin: 0
                }}
              >
                {messages.commonWaitTimeInfo}
              </p>
            </Box>
          );
        }

        const visibleEntries = getVisibleEntries(
          Boolean(values.overwriteExisting)
        );

        return (
          <>
            {!isCompleted && (
              <FormGroup
                label={messages.aiGenerateTranslationsExistingTranslations}
              >
                <RadioGroup
                  field="overwriteExisting"
                  options={overwriteOptions}
                />
              </FormGroup>
            )}

            {isCompleted && numberOfFailedTexts > 0 && (
              <Alert
                text={getMessageWithPlaceholders({
                  message: messages.aiGenerateTranslationsSomeFailed,
                  placeholders: { numberOfTexts: numberOfFailedTexts }
                })}
                type="warning-light"
              />
            )}

            {isLoading ? (
              <LoadingIndicator />
            ) : (
              <Box
                padding={0}
                style={{ marginTop: 16 }}
                title={getMessageWithPlaceholders({
                  message: isCompleted
                    ? messages.aiGenerateTranslationsTranslatedStrings
                    : messages.aiGenerateTranslationsTranslatableStrings,
                  placeholders: { numberOfTexts: visibleEntries.length }
                })}
              >
                {visibleEntries.length === 0 ? (
                  <EmptyIndicator
                    icon="language"
                    size="small"
                    title={messages.aiGenerateTranslationsNoTexts}
                  />
                ) : (
                  <div className="fixed">
                    <BoxTable>
                      <BoxTableHeader>
                        <BoxTableCell style={{ width: '50%' }}>
                          <FlagIcon
                            lang={course.language}
                            style={{ marginRight: 6 }}
                          />
                          {formatLanguageLabel(course.language)}
                        </BoxTableCell>
                        <BoxTableCell style={{ width: '50%' }}>
                          <FlagIcon
                            lang={targetLanguage}
                            style={{ marginRight: 6 }}
                          />
                          {formatLanguageLabel(targetLanguage)}
                        </BoxTableCell>
                      </BoxTableHeader>

                      {visibleEntries.map(([key, originalValue]) => (
                        <BoxTableRow key={key}>
                          <BoxTableCell verticalAlign="top">
                            <RichTextValue html={originalValue} />
                          </BoxTableCell>
                          <BoxTableCell verticalAlign="top">
                            <RichTextValue
                              html={translationData?.[key] ?? ''}
                            />
                          </BoxTableCell>
                        </BoxTableRow>
                      ))}
                    </BoxTable>
                  </div>
                )}

                <style jsx>{`
                  .fixed :global(.box-table) {
                    table-layout: fixed;
                  }
                `}</style>
              </Box>
            )}
          </>
        );
      }}
    </ModalButtonWithForm>
  );
};

export default AIGenerateTranslations;
